import { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
} from '@mui/material';
import { useNordaxActions } from '../useNordaxActions';

interface Duration {
    Length: number;
    Type: string;
}

interface LoanDetails {
    ApprovedAmount: number;
    EffectiveInterest: number;
    Interest: number;
    PeriodicAmount: number;
    TotalPeriodicAmount: number;
    AdministrationFee: number;
    InvoiceFee: number;
    Duration: Duration;
    AgreementCode: string;
    [key: string]: string | number | Duration;
}

interface NordaxData {
    status: string;
    bankName: string;
    message: string;
    data: {
        uuid: string;
        nordaxId: string;
        status: string;
        body?: {
            LoanDetails?: LoanDetails;
        };
    };
}

interface Props {
    data: NordaxData | null;
    onActionComplete?: () => void;
}

export const NordaxOffers = ({ data, onActionComplete }: Props) => {
    const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { acceptOffer, rejectOffer } = useNordaxActions();

    if (!data?.data) {
        return null;
    }

    const { uuid, status: s } = data.data;
    const status = s.toLowerCase();

    const handleAcceptOffer = async () => {
        try {
            await acceptOffer.mutateAsync({ uuid });
            setSuccessMessage('Offer accepted successfully');
            onActionComplete?.();
            // Reload page after 2 seconds
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error('Error accepting offer:', error);
        }
    };

    const handleRejectOffer = async () => {
        try {
            await rejectOffer.mutateAsync({ uuid });
            setConfirmRejectOpen(false);
            setSuccessMessage('Offer rejected successfully');
            onActionComplete?.();
            // Reload page after 2 seconds
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error('Error rejecting offer:', error);
        }
    };

    return (
        <>
            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Tilbud fra Nordax (current status: {status})
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        {status === 'approved' && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAcceptOffer}
                                disabled={acceptOffer.isLoading || rejectOffer.isLoading}
                            >
                                Aksepter
                            </Button>
                        )}
                        {!['cancelled', 'rejected'].includes(status) && (
                            <Button
                                variant="outlined"
                                color="error"
                            onClick={() => setConfirmRejectOpen(true)}
                            disabled={acceptOffer.isLoading || rejectOffer.isLoading}
                        >
                                Avslå
                            </Button>
                        )}
                    </Box>
                    {data.data.body?.LoanDetails && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Lånedetaljer
                            </Typography>
                            <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                                <Table aria-label="Loan Details Table" tabIndex={0}>
                                    <TableBody>
                                        {Object.entries(data.data.body.LoanDetails).map(([key, value]) => {
                                            if (key === 'Duration' && typeof value === 'object' && value !== null && 'Length' in value && 'Type' in value) {
                                                return (
                                                    <TableRow key={key}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{'Duration'}</TableCell>
                                                        <TableCell>{`${value.Length} ${value.Type}`}</TableCell>
                                                    </TableRow>
                                                );
                                            }
                                            if (typeof value === 'string' || typeof value === 'number') {
                                                return (
                                                    <TableRow key={key}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                                                        <TableCell>{value}</TableCell>
                                                    </TableRow>
                                                );
                                            }
                                            // Fallback for any other object type
                                            return (
                                                <TableRow key={key}>
                                                    <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                                                    <TableCell>{JSON.stringify(value)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Confirmation Dialog */}
            <Dialog open={confirmRejectOpen} onClose={() => setConfirmRejectOpen(false)}>
                <DialogTitle>Bekreft avslag</DialogTitle>
                <DialogContent>
                    Vil du avslå tilbudet fra Nordax?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmRejectOpen(false)}>Avbryt</Button>
                    <Button onClick={handleRejectOffer} color="error" autoFocus>
                        Bekreft avslag
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Message */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={2000}
                onClose={() => setSuccessMessage(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSuccessMessage(null)} 
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </>
    );
}; 